/**
 * Accounts Search Bar Component
 * Search bar for filtering accounts by name or ID
 */

import { useState, useEffect, useCallback, forwardRef } from 'react';
import { Input } from '@/core/components/ui/input';
import { Search, X } from 'lucide-react';
import { Button } from '@/core/components/ui/button';

interface AccountsSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const AccountsSearchBar = forwardRef<HTMLInputElement, AccountsSearchBarProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Search by Account Name or ID',
      className,
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState(value);

    // Sync local value when external value changes
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // Handle input change with instant search (client-side filtering)
    const handleInputChange = useCallback(
      (newValue: string) => {
        setLocalValue(newValue);
        onChange(newValue);
      },
      [onChange]
    );

    const handleClear = useCallback(() => {
      setLocalValue('');
      onChange('');
    }, [onChange]);

    return (
      <div className={`relative ${className}`}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={ref}
          value={localValue}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9 pr-9"
        />
        {localValue && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }
);

AccountsSearchBar.displayName = 'AccountsSearchBar';
